import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCalendarAlt, faClock, faUser, faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";

const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Get booking details from URL params (if available)
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    // Staggered animations
    setTimeout(() => setShowCheckmark(true), 300);
    setTimeout(() => setShowContent(true), 600);
    setTimeout(() => setShowDetails(true), 900);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-2xl w-full">
        {/* Confirmation Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-12 text-center relative overflow-hidden">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
            </div>

            {/* Animated Checkmark */}
            <div className={`relative z-10 transition-all duration-700 ${showCheckmark ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
              <div className="inline-block relative">
                {/* Outer Circle Animation */}
                <div className="absolute inset-0 rounded-full bg-white/20 animate-ping"></div>
                <div className="absolute inset-0 rounded-full bg-white/30 animate-pulse"></div>
                
                {/* Checkmark Icon */}
                <div className="relative bg-white rounded-full p-6 shadow-lg">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="text-6xl text-purple-600"
                  />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className={`mt-6 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h1 className="text-4xl font-bold text-white mb-2">
                Booking Confirmed!
              </h1>
              <p className="text-purple-100 text-lg">
                Your appointment has been successfully scheduled
              </p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="px-8 py-8">
            <div className={`transition-all duration-700 ${showDetails ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <div className="w-1 h-8 bg-purple-600 rounded-full mr-3"></div>
                What's Next?
              </h2>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon={faEnvelope} className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1 text-center">
                    <h3 className="font-semibold text-gray-900 mb-1">Check Your Email</h3>
                    <p className="text-gray-600 text-sm">
                      We've sent you a confirmation email with all the booking details
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1 text-center">
                    <h3 className="font-semibold text-gray-900 mb-1">Add to Calendar</h3>
                    <p className="text-gray-600 text-sm">
                      Don't forget to add this appointment to your calendar
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon={faClock} className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1 text-center">
                    <h3 className="font-semibold text-gray-900 mb-1">Reminder</h3>
                    <p className="text-gray-600 text-sm">
                      You'll receive a reminder before your appointment
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate("/")}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  Return Home
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-semibold rounded-lg transition-all duration-200"
                >
                  Print Confirmation
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Confetti Animation (Optional - can be removed if too much) */}
        <div className="fixed inset-0 pointer-events-none z-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-purple-400 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti {
          animation: confetti linear infinite;
        }
      `}</style>
    </div>
  );
};

export default BookingConfirmationPage;
